import { useEffect, useState } from "react";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { AxiosError } from "axios";

import type { AdminUser } from "@/api/admin";
import { appsApi, groupsApi, usersApi } from "@/api/admin";

type Feedback = { kind: "success" | "error"; text: string };

const PAGE_SIZE = 10;

function errText(error: unknown, fallback: string): string {
  if (error instanceof AxiosError) {
    return (
      (error.response?.data as { detail?: string })?.detail ??
      error.message ??
      fallback
    );
  }
  return fallback;
}

export function UsersPage() {
  const qc = useQueryClient();
  const [rolesFor, setRolesFor] = useState<AdminUser | null>(null);
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [page, setPage] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  // Debounce the search box so we don't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Any new search term starts from the first page.
  useEffect(() => {
    setPage(0);
  }, [search]);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["admin", "users", { page, search }],
    queryFn: () => usersApi.list(PAGE_SIZE, page * PAGE_SIZE, search),
    placeholderData: keepPreviousData,
  });

  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const rangeStart = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const rangeEnd = Math.min(total, page * PAGE_SIZE + (data?.users.length ?? 0));

  // If the current page falls past the end (e.g. after a filter narrows the
  // set or users are removed), snap back to the last valid page.
  useEffect(() => {
    if (page > 0 && page > pageCount - 1) setPage(pageCount - 1);
  }, [page, pageCount]);

  const setDisabled = useMutation({
    mutationFn: ({ id, disabled }: { id: string; disabled: boolean; name: string }) =>
      usersApi.setDisabled(id, disabled),
    onSuccess: (_res, vars) => {
      qc.invalidateQueries({ queryKey: ["admin", "users"] });
      setFeedback({
        kind: "success",
        text: vars.disabled
          ? `${vars.name} disabled. Their sessions were revoked and access to all Windmill apps is denied.`
          : `${vars.name} re-enabled.`,
      });
    },
    onError: (error, vars) =>
      setFeedback({
        kind: "error",
        text: errText(
          error,
          `Failed to ${vars.disabled ? "disable" : "enable"} ${vars.name}.`,
        ),
      }),
  });

  const revokeAll = useMutation({
    mutationFn: ({ id }: { id: string; name: string }) =>
      usersApi.revokeSessions(id),
    onSuccess: (res, vars) => {
      // last_login and session state can change — refresh the list too.
      qc.invalidateQueries({ queryKey: ["admin", "users"] });
      setFeedback({
        kind: "success",
        text:
          res.revoked > 0
            ? `Signed ${vars.name} out of ${res.revoked} active session${
                res.revoked === 1 ? "" : "s"
              }. They must log in again via SSO.`
            : `${vars.name} had no active sessions to revoke.`,
      });
    },
    onError: (error, vars) =>
      setFeedback({
        kind: "error",
        text: errText(error, `Failed to revoke sessions for ${vars.name}.`),
      }),
  });

  const pendingDisableId = setDisabled.isPending
    ? setDisabled.variables?.id
    : undefined;
  const pendingRevokeId = revokeAll.isPending
    ? revokeAll.variables?.id
    : undefined;

  return (
    <div>
      <header className="mb-6">
        <h1 className="font-display text-2xl text-wm-white">Users</h1>
        <p className="mt-1 text-sm text-wm-gray-100/60">
          {data
            ? search
              ? `${total} user${total === 1 ? "" : "s"} matching “${search}”.`
              : `${total} user${total === 1 ? "" : "s"} in the directory.`
            : "—"}
        </p>
      </header>

      {feedback && (
        <div
          role="status"
          className={`mb-4 flex items-start justify-between gap-4 rounded-md border px-3 py-2 text-sm ${
            feedback.kind === "success"
              ? "border-emerald-500/20 bg-emerald-500/10 text-emerald-200"
              : "border-red-500/20 bg-red-500/10 text-red-200"
          }`}
        >
          <span>{feedback.text}</span>
          <button
            onClick={() => setFeedback(null)}
            className="shrink-0 text-xs text-current/70 hover:text-current"
            aria-label="Dismiss"
          >
            ✕
          </button>
        </div>
      )}

      <AddUserForm />

      <div className="mb-4 flex items-center gap-2">
        <div className="relative flex-1 sm:max-w-xs">
          <input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by name or email…"
            aria-label="Search users by name or email"
            className="w-full rounded-md border border-white/10 bg-transparent px-3 py-1.5 text-sm text-wm-white placeholder:text-wm-gray-100/40"
          />
        </div>
        {search && (
          <button
            onClick={() => setSearchInput("")}
            className="rounded-md border border-white/10 px-2 py-1.5 text-xs text-wm-white hover:bg-white/5"
          >
            Clear
          </button>
        )}
      </div>

      {isLoading ? (
        <p className="text-sm text-wm-gray-100/60">Loading…</p>
      ) : total === 0 ? (
        <p className="text-sm text-wm-gray-100/60">
          {search ? `No users match “${search}”.` : "No users yet."}
        </p>
      ) : (
        <table className="w-full text-left text-sm">
          <thead className="text-xs uppercase tracking-wider text-wm-gray-100/60">
            <tr>
              <th className="py-2 pr-4">User</th>
              <th className="py-2 pr-4">Last login</th>
              <th className="py-2 pr-4">Status</th>
              <th className="py-2">&nbsp;</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {data?.users.map((u) => (
              <tr key={u.id}>
                <td className="py-3 pr-4">
                  <div className="text-wm-white">{u.name}</div>
                  <div className="text-xs text-wm-gray-100/60">{u.email}</div>
                </td>
                <td className="py-3 pr-4 text-wm-gray-100/80">
                  {u.last_login_at
                    ? new Date(u.last_login_at).toLocaleString()
                    : "—"}
                </td>
                <td className="py-3 pr-4">
                  {u.disabled ? (
                    <span className="rounded-full bg-red-500/10 px-2 py-0.5 text-xs text-red-300">
                      Disabled
                    </span>
                  ) : (
                    <span className="rounded-full bg-emerald-500/10 px-2 py-0.5 text-xs text-emerald-300">
                      Active
                    </span>
                  )}
                </td>
                <td className="py-3">
                  <div className="flex gap-2">
                    <button
                      onClick={() => setRolesFor(u)}
                      className="rounded-md border border-white/10 px-2 py-1 text-xs text-wm-white hover:bg-white/5"
                    >
                      Roles
                    </button>
                    <button
                      disabled={pendingDisableId === u.id}
                      onClick={() => {
                        setFeedback(null);
                        if (
                          u.disabled ||
                          window.confirm(
                            `Disable ${u.name}? This revokes their active sessions and blocks access to every Windmill app.`,
                          )
                        ) {
                          setDisabled.mutate({
                            id: u.id,
                            disabled: !u.disabled,
                            name: u.name,
                          });
                        }
                      }}
                      className="rounded-md border border-white/10 px-2 py-1 text-xs text-wm-white hover:bg-white/5 disabled:opacity-50"
                    >
                      {pendingDisableId === u.id
                        ? "…"
                        : u.disabled
                          ? "Enable"
                          : "Disable"}
                    </button>
                    <button
                      disabled={pendingRevokeId === u.id}
                      onClick={() => {
                        setFeedback(null);
                        if (
                          window.confirm(
                            `Sign ${u.name} out of all devices? They will need to log in again via SSO.`,
                          )
                        ) {
                          revokeAll.mutate({ id: u.id, name: u.name });
                        }
                      }}
                      className="rounded-md border border-white/10 px-2 py-1 text-xs text-wm-white hover:bg-white/5 disabled:opacity-50"
                    >
                      {pendingRevokeId === u.id ? "Revoking…" : "Revoke sessions"}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {!isLoading && total > 0 && (
        <div className="mt-4 flex items-center justify-between gap-4 text-sm text-wm-gray-100/60">
          <span>
            {rangeStart}–{rangeEnd} of {total}
            {isFetching && <span className="ml-2 text-wm-gray-100/40">…</span>}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="rounded-md border border-white/10 px-2 py-1 text-xs text-wm-white hover:bg-white/5 disabled:opacity-40"
            >
              ← Prev
            </button>
            <span className="text-xs text-wm-gray-100/60">
              Page {page + 1} of {pageCount}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
              disabled={page >= pageCount - 1}
              className="rounded-md border border-white/10 px-2 py-1 text-xs text-wm-white hover:bg-white/5 disabled:opacity-40"
            >
              Next →
            </button>
          </div>
        </div>
      )}

      {rolesFor && (
        <ManageRolesModal user={rolesFor} onClose={() => setRolesFor(null)} />
      )}
    </div>
  );
}

function ManageRolesModal({
  user,
  onClose,
}: {
  user: AdminUser;
  onClose: () => void;
}) {
  const qc = useQueryClient();

  const { data: appsData } = useQuery({
    queryKey: ["admin", "apps"],
    queryFn: () => appsApi.list(),
  });
  const { data: detail } = useQuery({
    queryKey: ["admin", "user", user.id],
    queryFn: () => usersApi.get(user.id),
  });

  const setRole = useMutation({
    mutationFn: ({ appSlug, role }: { appSlug: string; role: string | null }) =>
      usersApi.setRole(user.id, appSlug, role),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "user", user.id] });
      qc.invalidateQueries({ queryKey: ["admin", "grants"] });
    },
  });

  const roleFor = (slug: string) =>
    detail?.grants.find((g) => g.app_slug === slug)?.role ?? "";

  const errorMessage =
    setRole.error instanceof AxiosError
      ? (setRole.error.response?.data as { detail?: string })?.detail ??
        setRole.error.message
      : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-lg border border-white/10 bg-wm-black p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="mb-4">
          <h2 className="font-display text-xl text-wm-white">Manage roles</h2>
          <p className="mt-1 text-sm text-wm-gray-100/60">
            {user.name} · {user.email}
          </p>
          <p className="mt-1 text-xs text-wm-gray-100/40">
            Direct per-app roles. Group-based roles are managed on the group.
          </p>
        </header>

        {errorMessage && (
          <p className="mb-3 text-xs text-red-300">{errorMessage}</p>
        )}

        {!detail ? (
          <p className="text-sm text-wm-gray-100/60">Loading…</p>
        ) : (
          <div className="space-y-2">
            {appsData?.apps.map((app) => (
              <div
                key={app.id}
                className="flex items-center justify-between gap-3 rounded-md border border-white/5 px-3 py-2"
              >
                <span className="text-sm text-wm-white">{app.name}</span>
                <select
                  value={roleFor(app.slug)}
                  disabled={setRole.isPending}
                  onChange={(e) =>
                    setRole.mutate({
                      appSlug: app.slug,
                      role: e.target.value || null,
                    })
                  }
                  className="rounded-md border border-white/10 bg-wm-black px-2 py-1 text-sm text-wm-white"
                >
                  <option value="">— none —</option>
                  {app.roles.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        )}

        <div className="mt-5 flex justify-end">
          <button
            onClick={onClose}
            className="rounded-md border border-white/10 px-3 py-1.5 text-sm text-wm-white hover:bg-white/5"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

function AddUserForm() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [groupIds, setGroupIds] = useState<string[]>([]);

  const { data: groupsData } = useQuery({
    queryKey: ["admin", "groups"],
    queryFn: () => groupsApi.list(),
  });

  const create = useMutation({
    mutationFn: () =>
      usersApi.create({
        email: email.trim(),
        name: name.trim() || undefined,
        group_ids: groupIds,
      }),
    onSuccess: () => {
      setEmail("");
      setName("");
      setGroupIds([]);
      setOpen(false);
      qc.invalidateQueries({ queryKey: ["admin", "users"] });
    },
  });

  const toggleGroup = (id: string) =>
    setGroupIds((prev) =>
      prev.includes(id) ? prev.filter((g) => g !== id) : [...prev, id],
    );

  const errorMessage =
    create.error instanceof AxiosError
      ? (create.error.response?.data as { detail?: string })?.detail ??
        create.error.message
      : null;

  if (!open) {
    return (
      <div className="mb-6">
        <button
          onClick={() => setOpen(true)}
          className="rounded-md bg-wm-purple-bright px-3 py-1.5 text-sm text-wm-white"
        >
          + Add user
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (email.trim()) create.mutate();
      }}
      className="mb-8 space-y-4 rounded-lg border border-white/5 bg-wm-black/30 p-4"
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="block">
          <span className="mb-1 block text-xs uppercase tracking-wider text-wm-gray-100/60">
            Email
          </span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="jane.doe@windmill.digital"
            className="w-full rounded-md border border-white/10 bg-transparent px-3 py-1.5 text-sm text-wm-white placeholder:text-wm-gray-100/40"
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs uppercase tracking-wider text-wm-gray-100/60">
            Display name
          </span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Jane Doe"
            className="w-full rounded-md border border-white/10 bg-transparent px-3 py-1.5 text-sm text-wm-white placeholder:text-wm-gray-100/40"
          />
        </label>
      </div>

      {groupsData && groupsData.groups.length > 0 && (
        <div>
          <span className="mb-1 block text-xs uppercase tracking-wider text-wm-gray-100/60">
            Groups
          </span>
          <div className="flex flex-wrap gap-2">
            {groupsData.groups.map((g) => (
              <label
                key={g.id}
                className={`cursor-pointer rounded-full border px-3 py-1 text-xs ${
                  groupIds.includes(g.id)
                    ? "border-wm-purple-bright bg-wm-purple-bright/10 text-wm-purple-bright"
                    : "border-white/10 text-wm-gray-100/80"
                }`}
              >
                <input
                  type="checkbox"
                  checked={groupIds.includes(g.id)}
                  onChange={() => toggleGroup(g.id)}
                  className="sr-only"
                />
                {g.name}
              </label>
            ))}
          </div>
        </div>
      )}

      <p className="text-xs text-wm-gray-100/40">
        Assign roles after creation with the “Roles” action on the user row.
      </p>

      {errorMessage && (
        <p className="text-xs text-red-300">{errorMessage}</p>
      )}

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={!email.trim() || create.isPending}
          className="rounded-md bg-wm-purple-bright px-3 py-1.5 text-sm text-wm-white disabled:opacity-50"
        >
          {create.isPending ? "Creating…" : "Create user"}
        </button>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="rounded-md border border-white/10 px-3 py-1.5 text-sm text-wm-white hover:bg-white/5"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
